import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CustomButton from '../../../../components/CustomButton';
import CsvDateRangeDialog from '../../../../components/CsvDateRangeDialog';
import { tableCellClass, tableHeaderCellClass } from '../../../../components/TableCard';
import Permission from '../../../../common/permissions';
import { makeHasPermissionSelector, tenantSelector } from '../../../../selector/authenticationSelector';
import { stationCalendarDay } from '../../../../utils/stationTime';
import { downloadCsv } from '../../../../utils/csvDownload';
import { buildUtilisationCsv, utilisationCsvFileName } from '../../../../utils/utilisationCsv';
import {
    useGetTenantWindowsQuery,
    useLazyGetUtilisationQuery,
    useReleaseWindowDateMutation,
    useRestoreWindowDateMutation,
} from '../../../../api/organization/organizationApiSlice';
import type { Season, TenantWindow } from '../../../../api/organization/allocationWindowTypes';
import { WEEKDAY_KEYS } from '../../../organizations/allocationWindows/GrantWindowsEditor';
import useSeriesSeason from '../../../organizations/allocationWindows/useSeriesSeason';
import { formatDay, isDay, occurrenceDays, windowErrorMessage } from '../../../organizations/allocationWindows/windowDraft';
import SeasonPlanGrid from './SeasonPlanGrid';
import CopySeasonPanel from './CopySeasonPanel';
import ImportPlanPanel from './ImportPlanPanel';

const inputClass = 'rounded-md border border-foreground/15 px-2 py-1.5 text-sm bg-text-contrast text-foreground '
    + 'focus:outline-none focus:border-foreground/40';

/** How many coming dates the release picker offers. */
const RELEASE_CHOICES = 40;

const ALL = '';

/** The window's coming dates that are not released yet: what a release can name. */
const releasableDays = (window: TenantWindow, today: string): string[] =>
    occurrenceDays(window, today, RELEASE_CHOICES).filter((day) => !window.releaseDates.includes(day));

/** The released dates of a window, each with "take back" while it lies ahead. */
const ReleasedDates: React.FC<{
    window: TenantWindow;
    canRestore: boolean;
    today: string;
    busy: boolean;
    onRestore: (day: string) => void;
}> = ({ window, canRestore, today, busy, onRestore }) => {
    const { t, i18n } = useTranslation();
    const language: 'de' | 'en' = i18n.language?.startsWith('de') ? 'de' : 'en';
    if (window.releaseDates.length === 0) return <>{t('allocation_windows.none_released')}</>;
    return (
        <span className={'flex flex-wrap gap-x-3 gap-y-1'}>
            {window.releaseDates.map((day) => (
                <span key={day} className={'whitespace-nowrap'}>
                    {formatDay(day, language)}
                    {canRestore && day >= today && (
                        <button
                            type={'button'}
                            disabled={busy}
                            className={'ml-1 text-primary hover:underline'}
                            onClick={() => onRestore(day)}
                        >
                            {t('allocation_windows.restore.action')}
                        </button>
                    )}
                </span>
            ))}
        </span>
    );
};

/**
 * The allocations tab (M11, ADR-0027: "their own tab next to bookings").
 * Windows are not bookings, so they are not listed as bookings: per room a
 * season plan, and the windows with their released dates below it. The
 * tenant releases and restores single dates here, copies a season, and
 * imports a season's plan from a sheet (W2-3).
 *
 * Windows are EDITED on the organisation (Kunden → Organisation → Räume) and
 * on the room; this tab shows the whole tenant at once.
 */
const AllocationsTab: React.FC = () => {
    const { t, i18n } = useTranslation();
    const language: 'de' | 'en' = i18n.language?.startsWith('de') ? 'de' : 'en';
    const tenant = useSelector(tenantSelector);
    const canWrite = useSelector(makeHasPermissionSelector(Permission.writeCustomers()));
    const seriesSeason = useSeriesSeason();

    const [roomId, setRoomId] = useState(ALL);
    const [organizationId, setOrganizationId] = useState(ALL);
    // null = "as the series season", until the operator types one.
    const [seasonInput, setSeasonInput] = useState<Season | null>(null);
    const season = seasonInput ?? seriesSeason ?? { from: '', to: '' };
    const seasonSet = isDay(season.from) && isDay(season.to) && season.from <= season.to;

    const { data, isLoading, isError, refetch } = useGetTenantWindowsQuery(
        seasonSet ? { from: season.from, to: season.to } : undefined,
        { skip: !tenant },
    );
    const all = useMemo(() => data?.items ?? [], [data]);

    // The pickers list what the season holds, so a filter never offers an empty choice.
    const rooms = useMemo(() => {
        const map = new Map<string, string>();
        all.forEach((w) => map.set(w.groupId, w.groupName || t('common.not_applicable')));
        return Array.from(map, ([id, name]) => ({ id, name }));
    }, [all, t]);
    const organizations = useMemo(() => {
        const map = new Map<string, string>();
        all.forEach((w) => map.set(w.organizationId, w.organizationName || t('common.not_applicable')));
        return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
    }, [all, t]);

    const shown = all
        .filter((w) => roomId === ALL || w.groupId === roomId)
        .filter((w) => organizationId === ALL || w.organizationId === organizationId);
    const byRoom = useMemo(() => {
        const map = new Map<string, TenantWindow[]>();
        shown.forEach((w) => map.set(w.groupId, [...(map.get(w.groupId) ?? []), w]));
        return Array.from(map.values());
    }, [shown]);

    const [copyOpen, setCopyOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [releasing, setReleasing] = useState<{ window: TenantWindow, date: string, reason: string } | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [release, { isLoading: releasingBusy }] = useReleaseWindowDateMutation();
    const [restore, { isLoading: restoringBusy }] = useRestoreWindowDateMutation();
    const today = stationCalendarDay(new Date());

    const openRelease = (window: TenantWindow) => {
        setActionError(null);
        const first = releasableDays(window, today)[0];
        setReleasing({ window, date: first ?? '', reason: '' });
    };

    const submitRelease = async () => {
        if (!releasing?.date) return;
        setActionError(null);
        try {
            await release({
                organizationId: releasing.window.organizationId,
                grantId: releasing.window.grantId,
                windowId: releasing.window.windowId,
                date: releasing.date,
                reason: releasing.reason.trim() || undefined,
            }).unwrap();
            setReleasing(null);
        } catch (err) {
            setActionError(windowErrorMessage(err, t('allocation_windows.release.error')));
        }
    };

    const submitRestore = async (window: TenantWindow, date: string) => {
        setActionError(null);
        try {
            await restore({
                organizationId: window.organizationId, grantId: window.grantId, windowId: window.windowId, date,
            }).unwrap();
        } catch (err) {
            setActionError(windowErrorMessage(err, t('allocation_windows.restore.error')));
        }
    };

    // H-14 (#1811): planned vs. booked hours per organisation, room and month, as CSV.
    const [exportRange, setExportRange] = useState<Season | null>(null);
    const [exportMessage, setExportMessage] = useState<string | null>(null);
    const [fetchUtilisation, { isFetching: exporting }] = useLazyGetUtilisationQuery();

    const openExport = () => {
        setExportMessage(null);
        setExportRange(seasonSet ? season : { from: '', to: '' });
    };

    const submitExport = async () => {
        if (!exportRange || !isDay(exportRange.from) || !isDay(exportRange.to) || exportRange.from > exportRange.to) {
            setExportMessage(t('allocation_windows.utilisation.export_hint'));
            return;
        }
        const range = exportRange;
        setExportRange(null);
        setExportMessage(null);
        try {
            const report = await fetchUtilisation(range).unwrap();
            // An empty report is said, not handed over as an empty file.
            if (report.rows.length === 0) {
                setExportMessage(t('allocation_windows.utilisation.export_empty'));
                return;
            }
            const csv = buildUtilisationCsv(report, {
                organization: t('allocation_windows.utilisation.columns.organization'),
                room: t('allocation_windows.utilisation.columns.room'),
                month: t('allocation_windows.utilisation.columns.month'),
                kind: t('allocation_windows.utilisation.columns.kind'),
                plannedHours: t('allocation_windows.utilisation.columns.planned_hours'),
                releasedHours: t('allocation_windows.utilisation.columns.released_hours'),
                bookedHours: t('allocation_windows.utilisation.columns.booked_hours'),
                bookedInWindowHours: t('allocation_windows.utilisation.columns.booked_in_window_hours'),
                bookedOutsideWindowHours: t('allocation_windows.utilisation.columns.booked_outside_window_hours'),
                shareUsed: t('allocation_windows.utilisation.columns.share_used'),
                contractedHours: t('allocation_windows.utilisation.columns.contracted_hours'),
                notApplicable: t('common.not_applicable'),
                missing: t('common.missing'),
                kindLabel: (kind) => t(`allocation_windows.utilisation.kind.${kind}`),
            });
            downloadCsv(utilisationCsvFileName(t('allocation_windows.utilisation.file_name'), range.from, range.to), csv);
        } catch (err) {
            setExportMessage(windowErrorMessage(err, t('allocation_windows.utilisation.export_error')));
        }
    };

    const time = (w: TenantWindow) => t('allocation_windows.time', {
        weekday: t(`asset_detail.weekdays.${WEEKDAY_KEYS[w.weekday]}`), start: w.startTime, end: w.endTime,
    });

    return (
        <div className={'flex flex-col gap-4'}>
            <div className={'flex flex-wrap items-end gap-3 rounded-[5px] bg-text-contrast shadow-sm px-5 py-4'}>
                <label className={'flex flex-col gap-1 text-xs text-foreground/60'}>
                    {t('allocation_windows.filter.room')}
                    <select className={inputClass} value={roomId} onChange={(event) => setRoomId(event.target.value)}>
                        <option value={ALL}>{t('allocation_windows.filter.all_rooms')}</option>
                        {rooms.map((room) => <option key={room.id} value={room.id}>{room.name}</option>)}
                    </select>
                </label>
                <label className={'flex flex-col gap-1 text-xs text-foreground/60'}>
                    {t('allocation_windows.filter.organization')}
                    <select
                        className={inputClass}
                        value={organizationId}
                        onChange={(event) => setOrganizationId(event.target.value)}
                    >
                        <option value={ALL}>{t('allocation_windows.filter.all_organizations')}</option>
                        {organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                </label>
                <label className={'flex flex-col gap-1 text-xs text-foreground/60'}>
                    {t('allocation_windows.filter.season_from')}
                    <input
                        type={'date'}
                        className={inputClass}
                        value={season.from}
                        onChange={(event) => setSeasonInput({ ...season, from: event.target.value })}
                    />
                </label>
                <label className={'flex flex-col gap-1 text-xs text-foreground/60'}>
                    {t('allocation_windows.filter.season_to')}
                    <input
                        type={'date'}
                        className={inputClass}
                        value={season.to}
                        onChange={(event) => setSeasonInput({ ...season, to: event.target.value })}
                    />
                </label>
                <div className={'ml-auto flex flex-wrap gap-3'}>
                    <CustomButton
                        title={t('allocation_windows.utilisation.export')}
                        isFilled={false}
                        disabled={exporting}
                        onClick={openExport}
                    />
                    {canWrite && (
                        <CustomButton
                            title={t('allocation_windows.import.open')}
                            isFilled={false}
                            disabled={importOpen}
                            onClick={() => setImportOpen(true)}
                        />
                    )}
                    {canWrite && (
                        <CustomButton
                            title={t('allocation_windows.copy.open')}
                            isFilled={false}
                            disabled={!seasonSet || copyOpen}
                            onClick={() => setCopyOpen(true)}
                        />
                    )}
                </div>
            </div>
            {!seasonSet && (
                <p className={'text-sm text-foreground/60'}>{t('allocation_windows.filter.no_season')}</p>
            )}

            {importOpen && canWrite && (
                <ImportPlanPanel season={season} onClose={() => setImportOpen(false)} />
            )}

            {copyOpen && seasonSet && (
                <CopySeasonPanel
                    source={season}
                    groupIds={roomId === ALL ? [] : [roomId]}
                    organizationIds={organizationId === ALL ? [] : [organizationId]}
                    onClose={() => setCopyOpen(false)}
                />
            )}

            {exportMessage && (
                <p role={'alert'} className={'text-sm text-foreground'}>{exportMessage}</p>
            )}
            <CsvDateRangeDialog
                open={exportRange !== null}
                dateFrom={exportRange?.from ?? ''}
                dateTo={exportRange?.to ?? ''}
                onDateFromChange={(from) => setExportRange((range) => ({ from, to: range?.to ?? '' }))}
                onDateToChange={(to) => setExportRange((range) => ({ from: range?.from ?? '', to }))}
                allPeriodsHint={t('allocation_windows.utilisation.export_hint')}
                onCancel={() => setExportRange(null)}
                onExport={submitExport}
            />

            {actionError && (
                <p role={'alert'} className={'text-sm text-foreground'}>{actionError}</p>
            )}

            {isLoading && <p className={'text-sm text-foreground/60'}>{t('common.loading')}</p>}
            {/* An error is not "no windows" (A Failure Must Never Look Like an Absence). */}
            {isError && (
                <div role={'alert'} className={'flex items-center gap-3 text-sm text-foreground'}>
                    <span>{t('allocation_windows.load_error')}</span>
                    <CustomButton title={t('allocation_windows.retry')} isFilled={false} onClick={() => { refetch(); }} />
                </div>
            )}
            {!isLoading && !isError && shown.length === 0 && (
                <p className={'text-sm text-foreground/60'}>{t('allocation_windows.empty')}</p>
            )}

            {byRoom.map((windows) => (
                <section key={windows[0].groupId} className={'bg-text-contrast rounded-[5px] shadow-sm px-5 py-4 flex flex-col gap-4'}>
                    <h2 className={'text-lg font-semibold text-foreground'}>
                        {windows[0].groupName || t('common.not_applicable')}
                    </h2>
                    <SeasonPlanGrid windows={windows} />
                    <div className={'overflow-x-auto'}>
                        <table className={'w-full text-sm'}>
                            <thead>
                                <tr className={'border-b border-foreground/5'}>
                                    <th className={tableHeaderCellClass}>{t('allocation_windows.columns.organization')}</th>
                                    <th className={tableHeaderCellClass}>{t('allocation_windows.columns.time')}</th>
                                    <th className={tableHeaderCellClass}>{t('allocation_windows.columns.validity')}</th>
                                    <th className={tableHeaderCellClass}>{t('allocation_windows.columns.released')}</th>
                                    <th className={tableHeaderCellClass} />
                                </tr>
                            </thead>
                            <tbody>
                                {windows.map((w) => (
                                    <React.Fragment key={w.windowId}>
                                        <tr className={'border-b border-foreground/5 last:border-b-0'}>
                                            <td className={tableCellClass}>{w.organizationName || t('common.not_applicable')}</td>
                                            <td className={tableCellClass}>{time(w)}</td>
                                            <td className={tableCellClass}>
                                                {`${formatDay(w.validFrom, language)} – ${formatDay(w.validTo, language)}`}
                                            </td>
                                            <td className={tableCellClass}>
                                                <ReleasedDates
                                                    window={w}
                                                    canRestore={canWrite}
                                                    today={today}
                                                    busy={restoringBusy}
                                                    onRestore={(day) => submitRestore(w, day)}
                                                />
                                            </td>
                                            <td className={`${tableCellClass} text-right`}>
                                                {canWrite && w.validTo >= today && (
                                                    <button
                                                        type={'button'}
                                                        className={'text-primary hover:underline whitespace-nowrap'}
                                                        onClick={() => openRelease(w)}
                                                    >
                                                        {t('allocation_windows.release.action')}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                        {releasing?.window.windowId === w.windowId && (
                                            <tr className={'border-b border-foreground/5'}>
                                                <td colSpan={5} className={tableCellClass}>
                                                    <div className={'flex flex-col gap-3'}>
                                                        <p className={'text-sm text-foreground/60'}>
                                                            {t('allocation_windows.release.hint')}
                                                        </p>
                                                        <div className={'flex flex-wrap items-end gap-3'}>
                                                            <label className={'flex flex-col gap-1 text-xs text-foreground/60'}>
                                                                {t('allocation_windows.release.date')}
                                                                <select
                                                                    className={inputClass}
                                                                    value={releasing.date}
                                                                    onChange={(event) => setReleasing({ ...releasing, date: event.target.value })}
                                                                >
                                                                    {releasableDays(w, today).map((day) => (
                                                                        <option key={day} value={day}>{formatDay(day, language)}</option>
                                                                    ))}
                                                                </select>
                                                            </label>
                                                            <label className={'flex flex-col gap-1 text-xs text-foreground/60 grow'}>
                                                                {t('allocation_windows.release.reason')}
                                                                <input
                                                                    className={inputClass}
                                                                    value={releasing.reason}
                                                                    maxLength={500}
                                                                    onChange={(event) => setReleasing({ ...releasing, reason: event.target.value })}
                                                                />
                                                            </label>
                                                        </div>
                                                        <div className={'flex gap-3'}>
                                                            <CustomButton
                                                                title={t('common.cancel')}
                                                                isFilled={false}
                                                                onClick={() => setReleasing(null)}
                                                            />
                                                            <CustomButton
                                                                title={t('allocation_windows.release.confirm')}
                                                                loading={releasingBusy}
                                                                disabled={!releasing.date}
                                                                onClick={submitRelease}
                                                            />
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            ))}
        </div>
    );
};

export default AllocationsTab;
